<template>
  <!--
    The agent's microphone and speaker, behind one small button. A menu rather than two form selects:
    both lists, and the check on the device in use, are visible at once, and the speaker test sits
    right under the speaker it tests. Available in every state, a call included - a call is exactly
    when an agent finds out the device is wrong.
  -->
  <div
    class="softphone-audio-devices"
    data-cy="softphone-audio-devices"
    @keydown="onKeydown"
  >
    <b-button
      ref="toggle"
      class="softphone-audio-devices__toggle"
      type="button"
      size="sm"
      variant="outline-secondary"
      title="Microphone and speaker"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="softphone-audio-devices-menu"
      data-cy="softphone-audio-devices-toggle"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fa', 'microphone']" />
      <font-awesome-icon
        :icon="['fa', 'chevron-down']"
        class="softphone-audio-devices__chevron"
        :class="{ 'softphone-audio-devices__chevron--open': isOpen }"
      />
    </b-button>

    <div
      v-if="isOpen"
      id="softphone-audio-devices-menu"
      ref="menu"
      class="softphone-audio-devices__menu"
      role="menu"
      aria-label="Microphone and speaker"
      data-cy="softphone-audio-devices-menu"
    >
      <div
        v-if="isSoftphoneInAnotherTab"
        class="softphone-audio-devices__note"
        data-cy="softphone-audio-devices-other-tab"
      >
        Change your devices in the tab that is taking calls.
      </div>

      <!--
        Until microphone permission is granted the browser lists devices with blank names, which reads
        as broken. Offer the permission instead of a list of blanks.
      -->
      <div
        v-else-if="!hasLabels"
        data-cy="softphone-audio-devices-permission"
      >
        <div class="softphone-audio-devices__note">
          Allow microphone access to choose your devices.
        </div>

        <button
          type="button"
          role="menuitem"
          class="softphone-audio-devices__item"
          :disabled="isRequestingPermission"
          data-cy="softphone-audio-devices-allow"
          @click="onAllowMicrophone"
        >
          <span class="softphone-audio-devices__mark">
            <font-awesome-icon :icon="['fa', 'microphone']" />
          </span>
          <span class="softphone-audio-devices__label">Allow microphone</span>
          <b-spinner
            v-if="isRequestingPermission"
            small
            class="softphone-audio-devices__spinner"
          />
        </button>
      </div>

      <template v-else>
        <div
          role="group"
          aria-labelledby="softphone-audio-devices-input-header"
          data-cy="softphone-audio-input"
        >
          <div
            id="softphone-audio-devices-input-header"
            class="softphone-audio-devices__header"
          >
            Microphone
          </div>

          <template v-if="capabilities.canSelectInput">
            <button
              v-for="option in inputOptions"
              :key="`input-${option.value}`"
              type="button"
              role="menuitemradio"
              class="softphone-audio-devices__item"
              :class="{ 'softphone-audio-devices__item--selected': option.isSelected }"
              :aria-checked="option.isSelected ? 'true' : 'false'"
              :disabled="option.isDisabled || pending[kinds.INPUT] !== false"
              :title="option.text"
              :data-device-id="option.value || ''"
              data-cy="softphone-audio-input-option"
              @click="onSelect(kinds.INPUT, option)"
            >
              <span class="softphone-audio-devices__mark">
                <font-awesome-icon
                  v-if="option.isSelected"
                  :icon="['fa', 'check']"
                  class="softphone-audio-devices__check"
                />
              </span>
              <span class="softphone-audio-devices__label">{{ option.text }}</span>
              <b-spinner
                v-if="pending[kinds.INPUT] === option.value"
                small
                class="softphone-audio-devices__spinner"
              />
            </button>
          </template>

          <!-- Said rather than left out: a menu with no Microphone section reads as a bug. -->
          <div
            v-else
            class="softphone-audio-devices__note"
            data-cy="softphone-audio-input-none"
          >
            No microphone found. Plug one in to choose it.
          </div>

          <div
            class="softphone-audio-devices__divider"
            role="separator"
          />
        </div>

        <div
          role="group"
          aria-labelledby="softphone-audio-devices-output-header"
          data-cy="softphone-audio-output-group"
        >
          <div
            id="softphone-audio-devices-output-header"
            class="softphone-audio-devices__header"
          >
            Speaker
          </div>

          <template v-if="capabilities.canSelectOutput">
            <div data-cy="softphone-audio-output">
              <button
                v-for="option in outputOptions"
                :key="`output-${option.value}`"
                type="button"
                role="menuitemradio"
                class="softphone-audio-devices__item"
                :class="{ 'softphone-audio-devices__item--selected': option.isSelected }"
                :aria-checked="option.isSelected ? 'true' : 'false'"
                :disabled="option.isDisabled || pending[kinds.OUTPUT] !== false"
                :title="option.text"
                :data-device-id="option.value || ''"
                data-cy="softphone-audio-output-option"
                @click="onSelect(kinds.OUTPUT, option)"
              >
                <span class="softphone-audio-devices__mark">
                  <font-awesome-icon
                    v-if="option.isSelected"
                    :icon="['fa', 'check']"
                    class="softphone-audio-devices__check"
                  />
                </span>
                <span class="softphone-audio-devices__label">{{ option.text }}</span>
                <b-spinner
                  v-if="pending[kinds.OUTPUT] === option.value"
                  small
                  class="softphone-audio-devices__spinner"
                />
              </button>
            </div>

            <template v-if="canPlayTest">
              <div
                class="softphone-audio-devices__divider"
                role="separator"
              />

              <!-- Not during a call: a tune in the agent's ear mid-conversation is its own fault. -->
              <button
                type="button"
                role="menuitem"
                class="softphone-audio-devices__item"
                :disabled="isTestingSpeaker || isOnCall"
                :title="isOnCall ? 'Not available during a call' : 'Play a short tune on this speaker'"
                data-cy="softphone-speaker-test"
                @click="onTestSpeaker"
              >
                <span class="softphone-audio-devices__mark">
                  <font-awesome-icon :icon="['fa', 'volume-up']" />
                </span>
                <span class="softphone-audio-devices__label">
                  {{ isTestingSpeaker ? 'Playing...' : 'Test speaker' }}
                </span>
              </button>
            </template>
          </template>

          <div
            v-else
            class="softphone-audio-devices__note"
            data-cy="softphone-audio-output-unsupported"
          >
            The speaker follows your system sound settings.
          </div>
        </div>

        <!-- Echo cancellation works best when it hears the same device it is cancelling. -->
        <div
          v-if="isSplitAcrossDevices"
          class="softphone-audio-devices__note softphone-audio-devices__note--footer"
          data-cy="softphone-audio-devices-advice"
        >
          For the clearest calls, use the same headset for microphone and speaker.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage
} from '@/notification-utils'
import { listAudioDevices } from '@/services/softphone/audioDevices'
import {
  AUDIO_DEVICE_KINDS,
  SYSTEM_DEFAULT_LABEL,
  describeAudioDeviceSelection
} from '@/services/softphone/audioDeviceFeedback'
import { readAudioDeviceCapabilities } from '@/services/softphone/platformCapabilities'
import { canPlaySpeakerTest, playSpeakerTestTone } from '@/services/softphone/speakerTest'

const NOTIFY_BY_LEVEL = {
  success: showSuccessMessage,
  warning: showWarningMessage,
  error: showErrorMessage
}

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)'

function _hasDeviceChangeEvents() {
  return typeof navigator !== 'undefined'
    && typeof navigator.mediaDevices?.addEventListener === 'function'
}

export default {
  name: 'SoftphoneAudioDevices',

  data() {
    return {
      kinds: AUDIO_DEVICE_KINDS,
      isOpen: false,
      inputs: [],
      outputs: [],
      hasLabels: true,
      capabilities: { canSelectInput: true, canSelectOutput: true },
      // Per direction, the device being applied (`null` for System default) or `false` when idle,
      // so a microphone change in flight does not hold up a speaker change, or the other way round.
      pending: { [AUDIO_DEVICE_KINDS.INPUT]: false, [AUDIO_DEVICE_KINDS.OUTPUT]: false },
      isTestingSpeaker: false,
      isRequestingPermission: false
    }
  },

  computed: {
    ...mapState('softphone', ['audioDevices']),

    ...mapGetters('softphone', ['isCallActive', 'isRinging', 'isSoftphoneInAnotherTab']),

    inputOptions() {
      return this._options(this.inputs, this.audioDevices.inputDeviceId, this.audioDevices.inputLabel)
    },

    outputOptions() {
      return this._options(this.outputs, this.audioDevices.outputDeviceId, this.audioDevices.outputLabel)
    },

    isOnCall() {
      return this.isCallActive || this.isRinging
    },

    canPlayTest() {
      return canPlaySpeakerTest()
    },

    isSplitAcrossDevices() {
      const input = this.inputs.find(device => device.deviceId === this.audioDevices.inputDeviceId)
      const output = this.outputs.find(device => device.deviceId === this.audioDevices.outputDeviceId)

      return Boolean(input && output && input.groupId && output.groupId && input.groupId !== output.groupId)
    }
  },

  beforeDestroy() {
    this._stopListening()
  },

  methods: {
    ...mapActions('softphone', ['selectAudioInputDevice', 'selectAudioOutputDevice']),

    toggle() {
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },

    async open() {
      if (this.isOpen) {
        return
      }

      this.isOpen = true
      this._startListening()

      await this.refresh()
      await this.$nextTick()

      // The device in use, so the agent starts on the check rather than at the top of the list.
      const items = this._menuItems()
      const checked = items.find(item => item.getAttribute('aria-checked') === 'true')

      ;(checked || items[0])?.focus()
    },

    close({ restoreFocus = false } = {}) {
      if (!this.isOpen) {
        return
      }

      this.isOpen = false
      this._stopListening()

      if (restoreFocus) {
        this.$refs.toggle?.$el?.focus()
      }
    },

    async refresh() {
      const [devices, capabilities] = await Promise.all([
        listAudioDevices(),
        readAudioDeviceCapabilities()
      ])

      this.inputs = devices.inputs
      this.outputs = devices.outputs
      this.hasLabels = devices.hasLabels
      this.capabilities = capabilities
    },

    /**
     * "System default" first, then the devices. A stored choice that is not plugged in right now is
     * kept on the list, checked and greyed, so the menu does not silently check something else.
     */
    _options(devices, selectedId, selectedLabel) {
      const options = [
        { value: null, text: SYSTEM_DEFAULT_LABEL },
        ...devices.map(device => ({ value: device.deviceId, text: device.label || 'Unnamed device' }))
      ].map(option => ({ ...option, isSelected: option.value === (selectedId || null), isDisabled: false }))

      if (selectedId && !devices.some(device => device.deviceId === selectedId)) {
        options.push({
          value: selectedId,
          text: `${selectedLabel || 'Saved device'} (not connected)`,
          isSelected: true,
          isDisabled: true
        })
      }

      return options
    },

    async onSelect(kind, option) {
      if (option.isSelected || option.isDisabled || this.pending[kind] !== false) {
        return
      }

      const isInput = kind === AUDIO_DEVICE_KINDS.INPUT
      const deviceId = option.value
      const label = deviceId ? option.text : ''
      const previousLabel = isInput ? this.audioDevices.inputLabel : this.audioDevices.outputLabel

      this.pending[kind] = deviceId

      try {
        const select = isInput ? this.selectAudioInputDevice : this.selectAudioOutputDevice
        const result = await select({ deviceId, label })
        const { level, message } = describeAudioDeviceSelection({ kind, label, previousLabel, result })

        NOTIFY_BY_LEVEL[level](message)
      } finally {
        this.pending[kind] = false
      }
    },

    async onTestSpeaker() {
      try {
        this.isTestingSpeaker = true
        await playSpeakerTestTone(this.audioDevices.outputDeviceId)
      } catch (error) {
        console.warn('The softphone speaker test failed', error)
        showErrorMessage('Could not play the test sound on this speaker')
      } finally {
        this.isTestingSpeaker = false
      }
    },

    /**
     * Asks once, for nothing but the permission: the stream is stopped at once, so the microphone
     * light does not stay on. The device names are what the agent needed.
     */
    async onAllowMicrophone() {
      try {
        this.isRequestingPermission = true

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

        stream.getTracks().forEach(track => track.stop())

        await this.refresh()
      } catch (error) {
        showErrorMessage(error?.name === 'NotFoundError'
          ? 'No microphone was found. Plug one in and try again.'
          : 'Microphone access is blocked. Allow it in your browser\'s site settings.')
      } finally {
        this.isRequestingPermission = false
      }
    },

    /**
     * Menu keys. Escape is stopped here: the softphone panel this sits in closes on Escape too, and
     * one press should close only the innermost thing.
     */
    onKeydown(event) {
      if (!this.isOpen) {
        if (event.key === 'ArrowDown' && event.target === this.$refs.toggle?.$el) {
          event.preventDefault()
          this.open()
        }

        return
      }

      switch (event.key) {
      case 'Escape':
        event.stopPropagation()
        this.close({ restoreFocus: true })
        break
      case 'Tab':
        this.close()
        break
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
        event.preventDefault()
        this._moveFocus(event.key)
        break
      default:
        break
      }
    },

    _menuItems() {
      return this.$refs.menu ? Array.from(this.$refs.menu.querySelectorAll(MENU_ITEM_SELECTOR)) : []
    },

    _moveFocus(key) {
      const items = this._menuItems()

      if (!items.length) {
        return
      }

      const current = items.indexOf(document.activeElement)
      const last = items.length - 1
      const next = {
        Home: 0,
        End: last,
        ArrowDown: current < 0 || current === last ? 0 : current + 1,
        ArrowUp: current <= 0 ? last : current - 1
      }[key]

      items[next].focus()
    },

    _onDocumentPointerDown(event) {
      if (!this.$el.contains(event.target)) {
        this.close()
      }
    },

    _startListening() {
      document.addEventListener('pointerdown', this._onDocumentPointerDown)

      if (_hasDeviceChangeEvents()) {
        navigator.mediaDevices.addEventListener('devicechange', this.refresh)
      }
    },

    _stopListening() {
      document.removeEventListener('pointerdown', this._onDocumentPointerDown)

      if (_hasDeviceChangeEvents()) {
        navigator.mediaDevices.removeEventListener('devicechange', this.refresh)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.softphone-audio-devices {
  position: relative;
}

/*
 * Exactly as tall as Go offline across the badge: Bootstrap's small button is one line of its
 * 0.875rem type at 1.5, plus 0.25rem padding and the border on each side. Spelled out because icons
 * alone do not make a line box, so the button would otherwise shrink to the icons. Wider than tall
 * by Bootstrap's own 0.5rem side padding, so the two icons do not sit against the border.
 *
 * Content-box, so the border is added the way the browser draws it rather than as a fixed 2px: on a
 * scaled display a 1px border renders thinner, and a border-box size would come out taller.
 */
.softphone-audio-devices__toggle {
  $content: 0.875rem * 1.5;

  box-sizing: content-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: $content;
  height: $content;
  padding: 0.25rem 0.5rem;
}

.softphone-audio-devices__chevron {
  font-size: 0.65em;
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(180deg);
  }
}

/*
 * Hung off the button, over the panel it sits in. The panel's own surface and type colour, lifted
 * off it by a border and a shadow, so it reads as part of the softphone and not a browser popup.
 */
.softphone-audio-devices__menu {
  position: absolute;
  z-index: 10;
  top: calc(100% + 0.375rem);
  left: 0;
  width: 18.5rem;
  max-height: 24rem;
  overflow-y: auto;
  padding: 0.375rem 0;
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 0.5rem;
  background-color: $dropdown-bg;
  color: $dropdown-link-color;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.35);
  text-align: left;
}

.softphone-audio-devices__header {
  padding: 0.375rem 0.75rem 0.25rem 2.25rem;
  font-size: 0.8125rem;
  opacity: 0.65;
}

.softphone-audio-devices__item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.375rem 0.75rem 0.375rem 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 0.9375rem;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus {
    outline: none;
    background-color: rgba(128, 128, 128, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &--selected {
    font-weight: 600;
  }

  /* A checked device that is not plugged in is still the agent's choice, so it stays readable. */
  &--selected:disabled {
    opacity: 0.75;
  }
}

/* The column the check sits in, held open on every row so the labels line up. */
.softphone-audio-devices__mark {
  flex: 0 0 2.25rem;
  text-align: center;
}

.softphone-audio-devices__check {
  color: #28a745;
}

.softphone-audio-devices__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.softphone-audio-devices__spinner {
  flex: 0 0 auto;
  margin-left: 0.5rem;
}

.softphone-audio-devices__divider {
  height: 1px;
  margin: 0.375rem 0;
  background-color: rgba(128, 128, 128, 0.35);
}

.softphone-audio-devices__note {
  padding: 0.375rem 0.75rem 0.375rem 2.25rem;
  font-size: 0.8125rem;
  opacity: 0.75;

  &--footer {
    margin-top: 0.25rem;
    border-top: 1px solid rgba(128, 128, 128, 0.35);
    padding-top: 0.5rem;
  }
}
</style>
